import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { getDescriptionForPath } from '../../config/configEntry';
import {fetchDiscoveryIcons, uniqueIconsFromButtons,} from '../../services/admin/discoveryIconsApi';
import { uploadBotAsset } from '../../services/admin/botAdminApi';
import { withImageCacheBust } from './botBucketImageUrls';
import { removeLocalDiscoveryIcon } from './discoveryIconsLocalStorage';
import {
  appendButtonAtParent,
  emptyDiscoveryButton,
  getButtonAtPath,
  hasDiscoveryChildren,
  patchButtonAtPath,
  removeButtonAtPath,
} from './discoveryButtonsValidation';
import classes from './DiscoveryButtonsEditor.module.css';

function PenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 20h4l10.5-10.5a1.5 1.5 0 0 0-4.24-4.24L4 15.76V20z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 6.5l4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DiscoveryButtonRow({
  button,
  depth = 0,
  collapsed = false,
  onToggleCollapse,
  onEdit,
  onRemove,
  onAddChild,
}) {
  const { texts } = useSelector((state) => state.translate);
  const displayText = (button.text ?? '').trim() || texts.noButtonText;
  const isPlaceholder = !(button.text ?? '').trim();
  const childCount = Array.isArray(button.children) ? button.children.length : 0;
  const isParent = childCount > 0;

  return (
    <div
      className={classes.buttonRow}
      style={{ marginInlineStart: `${depth * 20}px` }}
    >
      {isParent ? (
        <button
          type="button"
          className={classes.collapseBtn}
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'פתח כפתורים משניים' : 'סגור כפתורים משניים'}
        >
          <span
            className={`${classes.collapseChevron} ${collapsed ? '' : classes.collapseChevronOpen}`}
            aria-hidden="true"
          >
            ▸
          </span>
        </button>
      ) : (
        <span className={classes.collapseSpacer} aria-hidden="true" />
      )}
      {depth > 0 ? <span className={classes.treeBranch} aria-hidden="true">↳</span> : null}
      <span
        className={`${classes.buttonRowText} ${isPlaceholder ? classes.buttonRowTextPlaceholder : ''}`}
      >
        {displayText}
        {isParent ? (
          <span className={classes.childBadge}> ({childCount})</span>
        ) : (
          <span className={classes.leafBadge}> עלה</span>
        )}
      </span>
      <div className={classes.buttonRowActions}>
        {onAddChild ? (
          <button
            type="button"
            className={classes.addChildBtn}
            onClick={onAddChild}
            title="הוסף כפתור משנה"
          >
            + משנה
          </button>
        ) : null}
        <button
          type="button"
          className={classes.iconBtn}
          onClick={onEdit}
          aria-label={texts.editButtonAriaLabel}
        >
          <PenIcon />
        </button>
        <button
          type="button"
          className={`${classes.iconBtn} ${classes.iconBtnDanger}`}
          onClick={onRemove}
          aria-label={texts.removeButtonAriaLabel}
        >
          <TrashIcon />
        </button>
      </div>
    </div>
  );
}

function DiscoveryButtonTreeList({
  buttons,
  parentPath = [],
  collapsedPaths,
  onToggleCollapse,
  onEdit,
  onRemove,
  onAddChild,
}) {
  if (!Array.isArray(buttons) || buttons.length === 0) {
    return null;
  }

  return buttons.map((button, index) => {
    const path = [...parentPath, index];
    const pathKey = path.join('-');
    const children = Array.isArray(button.children) ? button.children : [];
    const isParent = children.length > 0;
    const collapsed = isParent && collapsedPaths.has(pathKey);

    return (
      <div key={`discovery-tree-${pathKey}`} className={classes.treeNode}>
        <DiscoveryButtonRow
          button={button}
          depth={parentPath.length}
          collapsed={collapsed}
          onToggleCollapse={isParent ? () => onToggleCollapse(pathKey) : undefined}
          onEdit={() => onEdit(path)}
          onRemove={() => onRemove(path)}
          onAddChild={() => onAddChild(path)}
        />
        {isParent && !collapsed ? (
          <DiscoveryButtonTreeList
            buttons={children}
            parentPath={path}
            collapsedPaths={collapsedPaths}
            onToggleCollapse={onToggleCollapse}
            onEdit={onEdit}
            onRemove={onRemove}
            onAddChild={onAddChild}
          />
        ) : null}
      </div>
    );
  });
}

function DiscoveryButtonEditForm({
  pathKey,
  button,
  icons,
  iconsLoading,
  selectedIconMeta,
  bucketKey,
  domainName,
  labelIcon,
  labelText,
  labelMessage,
  onPatch,
  onIconsChange,
  onAddChild,
  onEditChild,
  onRemoveChild,
}) {
  const { texts } = useSelector((state) => state.translate);
  const fileInputRef = useRef(null);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const children = Array.isArray(button.children) ? button.children : [];
  const isLeaf = !hasDiscoveryChildren(button);

  const isSelectedIconLocal = useMemo(() => {
    if (!button.icon) return false;
    const meta = selectedIconMeta.get(button.icon);
    return meta?.source === 'local';
  }, [button.icon, selectedIconMeta]);

  const onUploadClick = () => {
    setUploadError('');
    fileInputRef.current?.click();
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadError('');

    const name = String(file.name || '').toLowerCase();
    if (!name.endsWith('.svg') && file.type !== 'image/svg+xml') {
      setUploadError(texts.uploadOnlySvg);
      return;
    }

    const maxBytes = 200 * 1024;
    if (typeof file.size === 'number' && file.size > maxBytes) {
      setUploadError(texts.fileTooLarge200kb);
      return;
    }

    let svgText = '';
    try {
      svgText = await readFileAsText(file);
    } catch {
      setUploadError(texts.fileReadFailed);
      return;
    }

    const normalizedSvg = svgText.replace(/^\uFEFF/, '').trim();
    if (!/<svg[\s>]/i.test(normalizedSvg)) {
      setUploadError(texts.invalidSvgFile);
      return;
    }

    if (!domainName) {
      setUploadError(texts.saveJsonFailed);
      return;
    }

    const baseName = String(file.name || 'icon').replace(/\.svg$/i, '') || 'icon';
    const fileName = `${baseName}.svg`;

    setUploading(true);
    try {
      const res = await uploadBotAsset({ domainName, fileName, file });
      if (!res?.ok) {
        throw new Error('upload failed');
      }
      const data = await res.json().catch(() => null);
      const url = typeof data?.url === 'string' ? data.url.trim() : '';
      if (!url) {
        throw new Error('missing url');
      }

      const displayUrl = withImageCacheBust(url);
      const uploaded = { url: displayUrl, label: baseName, fileName, source: 'bucket' };
      onIconsChange(mergeIconLists(icons, [uploaded]));
      onPatch({ icon: displayUrl });
    } catch {
      setUploadError(texts.saveJsonFailed);
    } finally {
      setUploading(false);
    }
  };

  const onRemoveLocal = () => {
    if (!button.icon) return;
    const meta = selectedIconMeta.get(button.icon);
    if (!meta || meta.source !== 'local') return;

    removeLocalDiscoveryIcon(bucketKey, meta.id);
    const nextIcons = icons.filter((it) => it.url !== meta.url);
    onIconsChange(nextIcons);
  };

  return (
    <>
      <div className={classes.iconPicker}>
        <label className={classes.Label}>{labelIcon}</label>
        {button.icon ?
          <div className={classes.iconPreviewRow}>
            <img src={button.icon} alt="" />
            <span>{selectedIconMeta.get(button.icon)?.label || button.icon}</span>
          </div>
          : null}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className={classes.Btn}
            onClick={onUploadClick}
            disabled={uploading}
          >
            {uploading ? texts.savingJsonToBucket : texts.uploadIconSvg}
          </button>
          {button.icon ?
            <button
              type="button"
              className={classes.Btn}
              onClick={() => onPatch({ icon: '' })}
              disabled={uploading}
            >
              {texts.removeIcon}
            </button>
            : null}
          {isSelectedIconLocal ?
            <button
              type="button"
              className={classes.Btn}
              onClick={onRemoveLocal}
              title={texts.removesFromLocalLibraryOnly}
              disabled={uploading}
            >
              {texts.removeFromLocalLibrary}
            </button>
            : null}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/svg+xml,.svg"
            style={{ display: 'none' }}
            onChange={onUploadFile}
          />
        </div>
        {uploadError ? <p className={classes.hintError}>{uploadError}</p> : null}
        {!iconsLoading && icons.length > 0 ?
          <div className={classes.iconGrid} role="listbox" aria-label={labelIcon}>
            {icons.map((item) => {
              const selected = button.icon === item.url;
              return (
                <button
                  key={item.url}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`${classes.iconOption} ${selected ? classes.iconOptionSelected : ''}`}
                  title={item.label}
                  onClick={() => onPatch({ icon: item.url })}
                >
                  <img src={item.url} alt={item.label} />
                </button>
              );
            })}
          </div>
          : null}
      </div>

      <div className={classes.Field}>
        <label className={classes.Label} htmlFor={`discovery-text-${pathKey}`}>
          {labelText}
        </label>
        <input
          id={`discovery-text-${pathKey}`}
          className={classes.TextInput}
          value={button.text ?? ''}
          onChange={(e) => onPatch({ text: e.target.value })}
        />
      </div>

      {isLeaf ?
        <div className={classes.Field}>
          <label className={classes.Label} htmlFor={`discovery-message-${pathKey}`}>
            {labelMessage}
          </label>
          <textarea
            id={`discovery-message-${pathKey}`}
            className={`${classes.Textarea} ${classes.messageInput}`}
            value={button.message ?? ''}
            onChange={(e) => onPatch({ message: e.target.value })}
          />
        </div>
        :
        <p className={classes.hint}>
          {texts.parentButtonNoMessageHint}
        </p>
      }

      <div className={classes.childrenSection}>
        <div className={classes.childrenHeader}>
          <span className={classes.Label} style={{ margin: 0 }}>
            {texts.secondaryButtons}
          </span>
          <button
            type="button"
            className={classes.Btn}
            onClick={onAddChild}
          >
            {texts.addSecondaryButton}
          </button>
        </div>
        {children.length === 0 ?
          <p className={classes.hint}>{texts.noSecondaryButtonsHint}</p>
          :
          <div className={classes.buttonList}>
            {children.map((child, childIndex) =>
              <DiscoveryButtonRow
                key={`discovery-child-${pathKey}-${childIndex}`}
                button={child}
                onEdit={() => onEditChild(childIndex)}
                onRemove={() => onRemoveChild(childIndex)}
              />
            )}
          </div>
        }
      </div>
    </>
  );
}

function pathLabel(path, buttons, texts) {
  if (!Array.isArray(path) || path.length === 0) return '';
  const parts = [];
  let list = buttons;
  for (let i = 0; i < path.length; i += 1) {
    const btn = list?.[path[i]];
    const text = (btn?.text ?? '').trim() || (texts.buttonNumberFallback || '').replace('{n}', String(path[i] + 1));
    parts.push(text);
    list = btn?.children;
  }
  return parts.join(' ← ');
}

function DiscoveryButtonEditModal({
  path,
  buttons,
  button,
  icons,
  iconsLoading,
  selectedIconMeta,
  bucketKey,
  domainName,
  labelIcon,
  labelText,
  labelMessage,
  onPatch,
  onIconsChange,
  onNavigateToPath,
  onAddChild,
  onRemoveChild,
  onClose,
  modalContainer,
}) {
  const { texts } = useSelector((state) => state.translate);
  const panelRef = useRef(null);
  const titleId = `discovery-modal-title-${path.join('-')}`;
  const pathKey = path.join('-');

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (path.length > 1) {
          onNavigateToPath(path.slice(0, -1));
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, onNavigateToPath, path]);

  useEffect(() => {
    const focusable = panelRef.current?.querySelector(
      `button:not([aria-label="${texts.closeAriaLabel}"]), input, textarea`
    );
    focusable?.focus();
  }, [pathKey, texts.closeAriaLabel]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!modalContainer) {
    return null;
  }

  return createPortal(
    <div
      className={classes.modalBackdrop}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={classes.modalPanel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={classes.modalHeader}>
          <div className={classes.modalTitleBlock}>
            {path.length > 1 ?
              <button
                type="button"
                className={classes.breadcrumbBack}
                onClick={() => onNavigateToPath(path.slice(0, -1))}
              >
                {texts.backToPreviousLevel}
              </button>
              : null}
            <p id={titleId} className={classes.modalTitle}>
              {pathLabel(path, buttons, texts)}
            </p>
          </div>
          <button
            type="button"
            className={classes.modalClose}
            onClick={onClose}
            aria-label={texts.closeAriaLabel}
          >
            ×
          </button>
        </div>

        <div className={classes.modalBody}>
          <DiscoveryButtonEditForm
            pathKey={pathKey}
            button={button}
            icons={icons}
            iconsLoading={iconsLoading}
            selectedIconMeta={selectedIconMeta}
            bucketKey={bucketKey}
            domainName={domainName}
            labelIcon={labelIcon}
            labelText={labelText}
            labelMessage={labelMessage}
            onPatch={onPatch}
            onIconsChange={onIconsChange}
            onAddChild={onAddChild}
            onEditChild={(childIndex) => onNavigateToPath([...path, childIndex])}
            onRemoveChild={(childIndex) => onRemoveChild([...path, childIndex])}
          />
        </div>
      </div>
    </div>,
    modalContainer
  );
}

export default function DiscoveryButtonsEditor({
  botName,
  domainName,
  draft,
  buttons,
  onChange,
  descriptions,
  modalContainer,
}) {
  const { texts } = useSelector((state) => state.translate);
  const [icons, setIcons] = useState([]);
  const [iconsLoading, setIconsLoading] = useState(true);
  const [iconsHint, setIconsHint] = useState('');
  const [bucketKey, setBucketKey] = useState('');
  const [editingPath, setEditingPath] = useState(null);
  const [collapsedPaths, setCollapsedPaths] = useState(() => new Set());

  const toggleCollapse = useCallback((pathKey) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) {
        next.delete(pathKey);
      } else {
        next.add(pathKey);
      }
      return next;
    });
  }, []);

  const expandPathKey = useCallback((pathKey) => {
    setCollapsedPaths((prev) => {
      if (!prev.has(pathKey)) return prev;
      const next = new Set(prev);
      next.delete(pathKey);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIconsLoading(true);
    setIconsHint('');

    fetchDiscoveryIcons(botName, draft).then(({ icons: loaded, source, bucketKey: loadedBucketKey }) => {
      if (cancelled) return;

      const fromButtons = uniqueIconsFromButtons(buttons);
      const merged = mergeIconLists(loaded, fromButtons);

      setIcons(merged);
      setIconsLoading(false);
      setBucketKey(loadedBucketKey || '');

      if (merged.length > 0 && source === 'fallback') {
        setIconsHint((texts.localIconsListBucket || '').replace('{bucketKey}', loadedBucketKey));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [botName, draft, buttons, texts.localIconsListBucket]);

  const labelText = getDescriptionForPath(
    descriptions,
    texts.discoveryButtonTextLabel,
    'discoveryButton.text'
  );
  const labelMessage = getDescriptionForPath(
    descriptions,
    texts.discoveryBotMessageLabel,
    'discoveryButton.message'
  );
  const labelIcon = getDescriptionForPath(
    descriptions,
    texts.discoveryIconLabel,
    'discoveryButton.icon'
  );

  const updateAtPath = useCallback(
    (path, patch) => {
      onChange(patchButtonAtPath(buttons, path, patch));
    },
    [buttons, onChange]
  );

  const addRootButton = () => {
    const { buttons: next, newPath } = appendButtonAtParent(
      buttons,
      [],
      emptyDiscoveryButton()
    );
    onChange(next);
    if (newPath) setEditingPath(newPath);
  };

  const removeButton = (path) => {
    onChange(removeButtonAtPath(buttons, path));
    setEditingPath((current) => {
      if (!current) return null;
      if (pathsEqual(current, path)) return null;
      return adjustPathAfterRemove(current, path);
    });
  };

  const addChildAtPath = (parentPath) => {
    const parentKey = parentPath.join('-');
    if (parentKey) {
      expandPathKey(parentKey);
    }
    const { buttons: next, newPath } = appendButtonAtParent(
      buttons,
      parentPath,
      emptyDiscoveryButton()
    );
    onChange(next);
    if (newPath) setEditingPath(newPath);
  };

  const addChildUnderEditing = () => {
    if (!editingPath) return;
    addChildAtPath(editingPath);
  };

  const closeModal = useCallback(() => {
    setEditingPath(null);
  }, []);

  const selectedIconMeta = useMemo(() => {
    const map = new Map(icons.map((item) => [item.url, item]));
    return map;
  }, [icons]);

  const editingButton =
    editingPath != null ? getButtonAtPath(buttons, editingPath) : null;

  useEffect(() => {
    if (editingPath != null && !getButtonAtPath(buttons, editingPath)) {
      setEditingPath(null);
    }
  }, [buttons, editingPath]);

  return (
    <section className={classes.Field}>
      <div className={classes.sectionHeader}>
        <div className={classes.SectionTitle} style={{ margin: 0 }}>
          {texts.discoveryButtonsSection}
        </div>
        <button
          type="button"
          className={classes.Btn}
          onClick={addRootButton}
        >
          {texts.addDiscoveryButton}
        </button>
      </div>

      {iconsLoading ? <p className={classes.loading}>{texts.loadingIcons}</p> : null}
      {!iconsLoading && iconsHint ? <p className={classes.hint}>{iconsHint}</p> : null}

      {buttons.length === 0 ?
        <p className={classes.hint}>{texts.noDiscoveryButtonsHint}</p>
        : null}

      {buttons.length > 0 ?
        <div className={classes.buttonList}>
          <DiscoveryButtonTreeList
            buttons={buttons}
            collapsedPaths={collapsedPaths}
            onToggleCollapse={toggleCollapse}
            onEdit={setEditingPath}
            onRemove={removeButton}
            onAddChild={addChildAtPath}
          />
        </div>
        : null}

      {editingButton !== null && editingPath !== null ?
        <DiscoveryButtonEditModal
          path={editingPath}
          buttons={buttons}
          button={editingButton}
          icons={icons}
          iconsLoading={iconsLoading}
          selectedIconMeta={selectedIconMeta}
          bucketKey={bucketKey}
          domainName={domainName}
          labelIcon={labelIcon}
          labelText={labelText}
          labelMessage={labelMessage}
          onPatch={(patch) => updateAtPath(editingPath, patch)}
          onIconsChange={setIcons}
          onNavigateToPath={setEditingPath}
          onAddChild={addChildUnderEditing}
          onRemoveChild={removeButton}
          onClose={closeModal}
          modalContainer={modalContainer}
        />
        : null}
    </section>
  );
}

function pathsEqual(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function adjustPathAfterRemove(currentPath, removedPath) {
  if (!Array.isArray(currentPath) || currentPath.length === 0) return null;

  if (currentPath.length >= removedPath.length) {
    let isPrefix = true;
    for (let i = 0; i < removedPath.length; i += 1) {
      if (currentPath[i] !== removedPath[i]) {
        isPrefix = false;
        break;
      }
    }
    if (isPrefix) return null;
  }

  const next = [...currentPath];
  const depth = removedPath.length - 1;
  if (depth < 0) return next;

  let samePrefix = true;
  for (let i = 0; i < depth; i += 1) {
    if (next[i] !== removedPath[i]) {
      samePrefix = false;
      break;
    }
  }

  if (samePrefix && next.length > depth && next[depth] > removedPath[depth]) {
    next[depth] -= 1;
  }

  return next;
}

function mergeIconLists(...lists) {
  const seen = new Set();
  const out = [];

  lists.flat().forEach((item) => {
    if (!item?.url || seen.has(item.url)) return;
    seen.add(item.url);
    out.push(item);
  });

  return out;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read failed'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(file);
  });
}

